/**
 * The admin approval queue contract (CHI-6), shared so the client can import the
 * error constants and row types instead of re-declaring them.
 *
 * The API speaks `pending`; the database state is `pending_review`. Only the
 * server's data-access layer knows the database name.
 */

/*
 * Machine-readable reasons, carried as the TRPCError `cause` and copied to
 * `error.data.reason` by the server's error formatter. Detect errors by reason,
 * not by message.
 */

/** Approve or re-open would exceed the event's seats. */
export const EVENT_FULL = "EVENT_FULL" as const;
export const EVENT_FULL_TRPC_CODE = "PRECONDITION_FAILED" as const;
export const EVENT_FULL_MESSAGE = "event full";

/** The caller's `expectedVersion` no longer matches the row. */
export const STALE_VERSION = "STALE_VERSION" as const;
export const STALE_VERSION_TRPC_CODE = "CONFLICT" as const;

/** The CHI-3 schema or lifecycle module is not present yet (TRPCError PRECONDITION_FAILED). */
export const SCHEMA_NOT_READY = "SCHEMA_NOT_READY" as const;

/**
 * `events.register` refused (TRPCError PRECONDITION_FAILED): the caller's row is
 * waitlisted on a special event. A repeat submit while pending_review is a
 * quiet success (same shape as a first submit that lands in review), not an
 * error. Only the admin queue can move a row out of review.
 */
export const REGISTRATION_WAITLISTED = "REGISTRATION_WAITLISTED" as const;

export type RegistrationApprovalErrorReason =
  | typeof EVENT_FULL
  | typeof STALE_VERSION
  | typeof SCHEMA_NOT_READY
  | typeof REGISTRATION_WAITLISTED;

export class RegistrationApprovalError extends Error {
  constructor(readonly reason: RegistrationApprovalErrorReason, options?: { cause?: unknown }) {
    super(reason, options);
    this.name = "RegistrationApprovalError";
  }
}

export type PendingRegistrationStatus = "pending" | "waitlisted" | "registered";

/** `listPending` filter. `waitlisted` lists only re-openable (admin-declined) rows. */
export type PendingQueueStatus = "pending" | "waitlisted";

/** `stateChangeReason` written by an admin decline; only such rows can be re-opened. */
export const ADMIN_DECLINE_REASON = "admin_decline" as const;

/** Actions the queue offers on a row: approve/decline while pending, reopen when admin-declined. */
export type AdminRegistrationAction = "approve" | "decline" | "reopen";

export type PendingRegistration = {
  /** The registration id; send it back as `registrationId`. */
  id: number;
  name: string;
  email: string;
  isMember: boolean;
  eventId: string;
  eventName: string;
  status: PendingRegistrationStatus;
  /** ISO 8601. */
  submittedAt: string;
  /** Send it back as `expectedVersion`. */
  reviewVersion: number;
  stateChangeReason: string | null;
  /** ISO 8601. */
  stateChangedAt: string | null;
  stateChangedByOpenId: string | null;
  /** Admin-only. True when the latest resubmit history row has priorReason = admin_decline. */
  previouslyDeclined: boolean;
  actions: AdminRegistrationAction[];
};

/** Every approval-queue (special) event, regardless of the list filters. */
export type PendingQueueEventSummary = {
  eventId: string;
  eventName: string;
  pendingCount: number;
  /** Null when the event has no capacity limit on record. */
  seatsRemaining: number | null;
};

/**
 * Forward-only keyset paging: oldest `submittedAt` first, `id` ascending as the
 * tie-break. `nextCursor` is opaque; pass it back unchanged. `hasMore` is true
 * exactly when `nextCursor` is not null.
 */
export type ListPendingResult = {
  items: PendingRegistration[];
  nextCursor: string | null;
  hasMore: boolean;
  total: number;
  events: PendingQueueEventSummary[];
};

/** Input of `events.approve`, `events.reopen` and `events.decline`. */
export type AdminTransitionInput = { registrationId: number; expectedVersion: number };

/** `status` is the registration's API status after the call. */
export type AdminTransitionResult<S extends PendingRegistrationStatus = PendingRegistrationStatus> = {
  ok: true;
  alreadyApplied: boolean;
  registrationId: number;
  status: S;
};

export type ApproveResult = AdminTransitionResult<"registered">;
export type ReopenResult = AdminTransitionResult<"registered">;
export type DeclineResult = AdminTransitionResult<"waitlisted">;

export const PENDING_QUEUE_DEFAULT_LIMIT = 25;
export const PENDING_QUEUE_MAX_LIMIT = 100;

/**
 * Special events cannot carry a price until pricing is decided. Replaces the
 * old off-by-default SPECIAL_APPROVAL_FREE_ONLY gate — this is always enforced.
 */
export const SPECIAL_EVENT_FREE_ONLY_MESSAGE =
  "special events are free-only until pricing is decided";
